/** @jsx jsx */
/** @jsxFrag null */
// eslint-disable-next-line no-restricted-imports
import {jsx, ParamComponent} from 'panel';
import isEqual from 'lodash/isEqual';

import {IronContextRegistry} from 'common/report/context-registry';
import {SegmentFilterController, SelectMode} from 'common/charts/contexts/segment-filter-controller';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {DisplayOptions, InsightsBookmarkSections} from 'common/report/insights/util/types';
import {InsightsBookmarkParams, InsightsLegend} from 'common/types/reports/bookmark';
import {ThemesResource} from 'common/report/model-resources/themes';
import {getDisplayOptions} from 'common/report/insights/util';
import {isFlattenedData} from 'common/report/insights/util/chart';

import './index.styl';

type ChartRootJsxProps = {
  params?: {
    /** flows report includes chartType */
    bookmarkParams?: InsightsBookmarkParams & {chartType: string};
    onBookmarkUpdate?: (args: {params: InsightsBookmarkParams; requery: boolean; saveBoardOverrides?: boolean}) => void;
  };
};

/**
 * This component serves as the context provider for all chart types.
 *
 * It is important that every chart creates a new instance of `SegmentFilterController`
 * since legend & table data are local to each chart. SegmentFilterController must never
 * be initialized at the root level.
 *
 * @todo remove `segmentFilterController` from IronContextRegistry so that the above requirement
 * is type enforced
 */

export class ChartRoot extends ParamComponent<ChartRootJsxProps[`params`], EmptyObj, IronContextRegistry> {
  get config() {
    return {
      template: () => <div />,
      contexts: [`segmentFilterController` as const, `currentProject` as const, `themes` as const],
      defaultContexts: {
        segmentFilterController: new SegmentFilterController(),
      },
      params: {
        bookmarkParams: Object,
        onBookmarkUpdate: Function,
      },
      hooks: {
        preUpdate: (_newState: any, newParams?: ChartRootJsxProps[`params`]) => {
          if (!newParams) {
            return;
          }

          if (newParams.bookmarkParams) {
            // this does not currently run in download mode (?download=1), so it is called in connectedCallback too
            this.setThemeAndSegmentColorOverrides(newParams.bookmarkParams);

            /** makes sure legend selection changes to "Top N" when segments change */
            if (
              this.params!.bookmarkParams &&
              this.shouldResetSegmentFilterSelectMode(this.params!.bookmarkParams, newParams.bookmarkParams)
            ) {
              this.segmentFilterController.selectMode = SelectMode.Position;
            }
          }
        },
      },
    };
  }

  resourceCustomSubscriptions = new Map([
    [
      ThemesResource,
      () => {
        this.getContext(`segmentFilterController`).updateConnectedComponents();
        this.update();
      },
    ],
  ]);

  connectedCallback(): void {
    this.segmentFilterController.themesResource = this.getContext(`themes`);
    this.segmentFilterController.handleLegendChanges = this.handleLegendChange;
    super.connectedCallback();

    if (this.params?.bookmarkParams) {
      // this was needed in download mode (?download=1) because the preUpdate hook is never run
      this.setThemeAndSegmentColorOverrides(this.params.bookmarkParams);
    }
  }

  get segmentFilterController() {
    return this.getContext(`segmentFilterController`);
  }

  setThemeAndSegmentColorOverrides(bookmarkParams: InsightsBookmarkParams) {
    this.segmentFilterController.setReportTheme(bookmarkParams.displayOptions?.theme ?? null);
    this.segmentFilterController.localOverrides = bookmarkParams.legend?.overrides;
  }

  shouldResetSegmentFilterSelectMode(original: InsightsBookmarkParams, edited: InsightsBookmarkParams) {
    if (
      original.sections?.cohorts &&
      original.sections?.group &&
      edited?.sections?.cohorts &&
      edited?.sections?.group
    ) {
      const cohortsChanged = !isEqual(original.sections.cohorts, edited.sections.cohorts);
      const groupsChanged = !isEqual(original.sections.group, edited.sections.group);

      return cohortsChanged || groupsChanged;
    }

    return false;
  }

  /**
   * Legend interactions that update bookmarks:
   * - Color assignment
   * - Legend item selection changes
   */
  handleLegendChange = () => {
    const bookmarkParams = this.params!.bookmarkParams;
    if (!bookmarkParams) {
      return;
    }
    // Flows charts don't call segmentFilterController#loadSections
    const isFlows = bookmarkParams?.chartType === `sankey`;
    if (!this.segmentFilterController.hasLoaded() && !isFlows) {
      return;
    }

    let legend: InsightsLegend = {};
    if (isFlows) {
      // Flows charts don't have a selectable legend; params.legend is currently only used for color assignment
      // (params.legend.overrides). Resetting the legend on breakdown change is handled by
      // FlowsQueryBuilder#bookmarkUpdateFromQbUpdate
      const existingOverrides = bookmarkParams?.legend?.overrides;
      legend = {
        overrides: {...(existingOverrides ? existingOverrides : {}), ...this.segmentFilterController.localOverrides},
      };
    } else {
      const savedLegend = this.segmentFilterController.toBookmarkSavedLegend();
      const chartType = getDisplayOptions(
        // TODO - go/j/AE-796: Replace these with Pydantic-generated counterparts once fully defined and remove typecasts
        bookmarkParams.sections as InsightsBookmarkSections,
        bookmarkParams.displayOptions as DisplayOptions,
      ).chartType;

      const isFlat = isFlattenedData(chartType);

      legend = {
        overrides: this.segmentFilterController.localOverrides,
      };

      if (isFlat) {
        legend.individualSelectedSegments = savedLegend[0];
      } else {
        legend.filteredBreakdownSegments = savedLegend;
      }
    }
    const legendOverrides = this.getContext(`themes`).legendOverrides || {};
    this.params!.onBookmarkUpdate?.({
      params: {...bookmarkParams, legend},
      // applies legend overrides for the whole board
      saveBoardOverrides: Object.keys(legendOverrides).length > 0,
      requery: false,
    });
  };
}

export const ChartRootEl = defineAndMakeJsxFactory<ChartRootJsxProps>(`mp-chart-root`, ChartRoot);
export const ChartRootReact = panel2react<ChartRootJsxProps>(`mp-chart-root`, ChartRoot);
